import { lazy, Suspense } from 'react'
import { Route } from 'react-router'
import { agentRouteChildren } from '@/client/routes'
import { AgentSkeleton } from '@/skeleton'
import { PageLoading } from '~/shell/page-loading'

/**
 * The module is lazy, so a customer who only opens Analytics never downloads
 * the Agent's provider stack — Prism, PostHog and the workspace socket among
 * them.
 */
const AgentModule = lazy(() => import('@/client/module'))

/** Lazy for the same reason, and separately: help brings the Agent's stylesheets, nothing else. */
const AgentHelp = lazy(() => import('@/client/help'))

/** The Agent's half of the shell's left column, lazy so a reader who stays in
 *  Analytics downloads none of it. */
const AgentNav = lazy(() => import('@/client/navigation'))
const AnalysisPanel = lazy(() => import('@/client/analysis-panel'))

/** What the shell renders in the sidebar while the reader is in the Agent. */
export function agentNavigation() {
  return (
    <Suspense fallback={null}>
      <AgentNav />
    </Suspense>
  )
}

/**
 * The Agent in the column beside an Analytics screen. Lazy for the reason the
 * navigation is: a reader who never opens it never downloads the chat.
 */
export function agentAnalysisPanel(onClose: () => void) {
  return (
    <Suspense fallback={null}>
      <AnalysisPanel onClose={onClose} />
    </Suspense>
  )
}

/**
 * The Agent's in-product help, at `/help`.
 *
 * Above the brand and outside the session gate, because it is neither: nothing
 * on it is about one brand, and somebody who cannot sign in is exactly the
 * reader most likely to want it. `DOCS_URL` in the Agent tree is this path.
 */
export function agentHelpRoute() {
  return (
    <Route
      path='/help/*'
      element={
        <Suspense fallback={<PageLoading />}>
          <AgentHelp />
        </Suspense>
      }
    />
  )
}

/**
 * The Agent, mounted under `/w/:brandId/agent`.
 *
 * The screens themselves are `agentRouteChildren`, which lives in the Agent
 * tree beside the files it names. This is the mount point and the module's
 * boundary with the shell, and deliberately nothing else: a second copy of the
 * table here is a table that drifts, which is exactly what it did back when the
 * Agent also had a standalone entry of its own.
 */
export function agentRoutes() {
  return (
    <Route
      path='agent'
      element={
        <Suspense fallback={<AgentSkeleton />}>
          <AgentModule />
        </Suspense>
      }
    >
      {agentRouteChildren()}
    </Route>
  )
}
